import { saveAs } from "file-saver";

const CONTENT_TYPES = {
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

const SERVER_ERROR_MESSAGES = new Set([
  "internal server error.",
  "server-error",
]);
const CONTENT_DISPOSITION_UTF8_FILENAME_REGEX = /filename\*=UTF-8''([^;]+)/i;
const CONTENT_DISPOSITION_ASCII_FILENAME_REGEX = /filename="?([^";]+)"?/i;

export const normalizeExportFileType = (fileType = "xlsx") =>
  fileType === "excel" ? "xlsx" : fileType;

export const getReportExportFallbackFileName = ({
  reportName = "Report",
  fileType = "xlsx",
} = {}) => {
  const extension = normalizeExportFileType(fileType);
  const safeReportName =
    String(reportName || "Report")
      .trim()
      .replace(/\s+/g, "")
      .replace(/[^\w-]/g, "") || "Report";

  return `${safeReportName}-${new Date()
    .toISOString()
    .replace(/[:.]/g, "-")}.${extension}`;
};

export const getDownloadFileName = (headers, fallbackFileName) => {
  const contentDisposition =
    headers?.["content-disposition"] || headers?.["Content-Disposition"];

  if (!contentDisposition) {
    return fallbackFileName;
  }

  const utf8Match = contentDisposition.match(
    CONTENT_DISPOSITION_UTF8_FILENAME_REGEX,
  );
  const asciiMatch = utf8Match
    ? null
    : contentDisposition.match(CONTENT_DISPOSITION_ASCII_FILENAME_REGEX);
  const matchedName = utf8Match?.[1] || asciiMatch?.[1];

  if (!matchedName) {
    return fallbackFileName;
  }

  try {
    return decodeURIComponent(matchedName);
  } catch {
    return matchedName;
  }
};

export const getReportExportErrorMessage = (
  message,
  { noRecordsMessage, serverErrorMessage } = {},
) => {
  const normalizedMessage = typeof message === "string" ? message.trim() : "";
  const normalizedKey = normalizedMessage.toLowerCase();

  if (normalizedKey === "no-records-found") {
    return noRecordsMessage || normalizedMessage;
  }

  if (SERVER_ERROR_MESSAGES.has(normalizedKey)) {
    return serverErrorMessage || normalizedMessage;
  }

  return normalizedMessage || serverErrorMessage || "Server error";
};

export const downloadBlobResponse = (
  response,
  fallbackFileName,
  fileType = "xlsx",
) => {
  const extension = normalizeExportFileType(fileType);
  const contentType = CONTENT_TYPES[extension] || CONTENT_TYPES.xlsx;
  const fileBlob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType });
  const fileName = getDownloadFileName(response.headers, fallbackFileName);

  saveAs(fileBlob, fileName);
  return fileName;
};

export const downloadReportExport = (
  response,
  { reportName = "Report", fileType = "xlsx" } = {},
) => {
  const normalizedFileType = normalizeExportFileType(fileType);

  return downloadBlobResponse(
    response,
    getReportExportFallbackFileName({ reportName, fileType: normalizedFileType }),
    normalizedFileType,
  );
};
